import { CustomInfoSquareRoundedIcon } from "@/icons/custom/info-square-rounded";
import React, {
  cloneElement,
  createContext,
  forwardRef,
  useContext,
} from "react";

import { cn } from "@oneleet/utils";

import { type VariantProps, cva } from "class-variance-authority";

const DEFAULT_COLOR = "neutral";

const calloutVariants = cva("rounded-lg border px-3.5 py-2.5", {
  variants: {
    color: {
      neutral: "border-neutral-300 bg-neutral-100 text-neutral-950",
      accent: "text-accent-950 border-accent-400 bg-accent-100",
      green: "text-ng-green-950 border-ng-green-400 bg-ng-green-100",
      red: "text-ng-red-950 border-ng-red-400 bg-ng-red-100",
      orange: "text-ng-orange-950 border-ng-orange-400 bg-ng-orange-100",
      yellow: "text-ng-yellow-950 border-ng-yellow-400 bg-ng-yellow-100",
    },
  },
  defaultVariants: {
    color: DEFAULT_COLOR,
  },
});

const iconVariants = cva("size-4 shrink-0", {
  variants: {
    color: {
      neutral: "text-neutral-950",
      accent: "text-accent-950",
      green: "text-ng-green-950",
      red: "text-ng-red-950",
      orange: "text-ng-orange-950",
      yellow: "text-ng-yellow-950",
    },
  },
});

const NgCalloutContext = createContext<
  "neutral" | "accent" | "green" | "red" | "orange" | "yellow"
>(DEFAULT_COLOR);

type CalloutVariantProps = VariantProps<typeof calloutVariants>;

type NgCalloutProps = CalloutVariantProps &
  React.BaseHTMLAttributes<HTMLDivElement>;

interface NgCalloutComponent
  extends React.ForwardRefExoticComponent<
    NgCalloutProps & React.RefAttributes<HTMLDivElement>
  > {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Heading: typeof NgCalloutHeading;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Description: typeof NgCalloutDescription;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Icon: typeof NgCalloutIcon;
}

export const NgCallout = forwardRef<HTMLDivElement, NgCalloutProps>(
  ({ color = DEFAULT_COLOR, className, children, ...rest }, ref) => {
    return (
      <NgCalloutContext.Provider value={color}>
        <div
          ref={ref}
          className={cn(
            calloutVariants({
              color,
              className,
            }),
          )}
          {...rest}
        >
          {children}
        </div>
      </NgCalloutContext.Provider>
    );
  },
) as NgCalloutComponent;
NgCallout.displayName = "NgCallout";

export const NgCalloutHeading = forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...rest }, ref) => {
  return (
    <h3
      ref={ref}
      className={cn("text-ng-md font-ng-medium", className)}
      {...rest}
    />
  );
});
NgCalloutHeading.displayName = "NgCalloutHeading";
NgCallout.Heading = NgCalloutHeading;

export const NgCalloutDescription = forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...rest }, ref) => {
  return (
    <p
      ref={ref}
      className={cn("text-ng-md w-full max-w-screen-lg text-pretty", className)}
      {...rest}
    />
  );
});
NgCalloutDescription.displayName = "NgCalloutDescription";
NgCallout.Description = NgCalloutDescription;

export const NgCalloutIcon = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    children?: React.ReactElement<{ className?: string }>;
  }
>(({ className, children = <CustomInfoSquareRoundedIcon />, ...rest }, ref) => {
  const color = useContext(NgCalloutContext) || DEFAULT_COLOR;

  return (
    <div
      ref={ref}
      className={cn("flex h-5 shrink-0 items-center justify-center", className)}
      {...rest}
    >
      {cloneElement(children, {
        className: cn(
          iconVariants({
            color,
            className,
          }),
        ),
      })}
    </div>
  );
});
NgCalloutIcon.displayName = "NgCalloutIcon";
NgCallout.Icon = NgCalloutIcon;
